import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Megaphone, Sparkles, ShoppingBag, Target, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Megaphone,
    title: "BTL Marketing",
    description:
      "From guerrilla campaigns to large-scale brand activations, we create BTL strategies that demand attention.",
    image:
      "https://images.unsplash.com/photo-1501612780327-45045538702b?auto=format&fit=crop&w=1200&q=60",
    accent: "#1A56DB",
  },
  {
    icon: Sparkles,
    title: "Experiential Events",
    description:
      "Immersive brand experiences and live events that audiences remember long after the moment.",
    image:
      "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=60",
    accent: "#60A5FA",
  },
  {
    icon: ShoppingBag,
    title: "In-Store Promotions",
    description:
      "End-cap displays, sampling drives, and promoter programmes that convert footfall to sales.",
    image:
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=60",
    accent: "#E02424",
  },
  {
    icon: Target,
    title: "Brand Activation Strategy",
    description:
      "Full-funnel BTL strategy, channel planning, and execution roadmaps for ambitious brands.",
    image:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=60",
    accent: "#1A56DB",
  },
];

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 px-5 sm:px-8 lg:px-12 bg-[#070B18]"
      data-testid="services-section"
    >
      <div className="absolute inset-0 axe-grid opacity-30" aria-hidden="true" />

      <div ref={ref} className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14 sm:mb-20"
        >
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#60A5FA] font-semibold mb-4">
              <span className="inline-block h-px w-8 align-middle bg-[#60A5FA] mr-3" />
              Capabilities
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]"
              data-testid="services-headline"
            >
              What We Do.
            </h2>
          </div>
          <p className="max-w-md text-white/60 text-base sm:text-lg">
            Four sharp capabilities. One objective — break through the noise and
            move the metric that matters.
          </p>
        </motion.div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6"
          data-testid="services-grid"
        >
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  delay: 0.15 + i * 0.12,
                  duration: 0.7,
                  ease: [0.2, 0.8, 0.2, 1],
                }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-2xl bg-[#1E2A4A] border border-white/5 p-7 sm:p-9 lg:p-10 hover:border-[#60A5FA]/40 hover:shadow-[0_30px_80px_-30px_rgba(26,86,219,0.5)] transition-all duration-500"
                data-testid={`service-card-${i}`}
              >
                <span
                  className="absolute top-0 left-0 right-0 h-[2px]"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${s.accent}, transparent)`,
                  }}
                  aria-hidden="true"
                />

                {/* Hover image backdrop */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(30,42,74,0.9), rgba(30,42,74,0.96)), url(${s.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "grayscale(30%) saturate(110%)",
                  }}
                  aria-hidden="true"
                />

                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div
                      className="inline-flex items-center justify-center h-12 w-12 rounded-xl border border-white/10 bg-[#0A0F1E]/70 text-white group-hover:border-[#60A5FA]/50 transition-colors"
                      style={{ color: s.accent }}
                    >
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                    <span className="text-xs font-mono text-white/30">
                      0{i + 1}
                    </span>
                  </div>

                  <h3 className="mt-7 font-display text-2xl sm:text-3xl font-bold text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-white/65 text-[15px] sm:text-base leading-relaxed">
                    {s.description}
                  </p>

                  <div className="mt-8 inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-white/50 group-hover:text-white transition-colors">
                    Explore
                    <ArrowUpRight
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
